import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { CalendarDays, CheckCircle2, Clock, Users, LogOut, Trash2, MessageSquare, KeyRound, X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { toast } from "sonner";
import { buildAdminConfirmationUrl } from "@/lib/clinic";

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, paid: 0 });
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("appointments");
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    if (ready && !user) nav("/admin");
  }, [ready, user, nav]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [s, a, m] = await Promise.all([
        api.get("/appointments/stats"),
        api.get("/appointments"),
        api.get("/contact"),
      ]);
      setStats(s.data);
      setAppointments(a.data);
      setMessages(m.data);
    } catch (err) {
      console.error("Failed to load admin data:", err);
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const removeAppt = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  if (!ready || !user) return null;

  return (
    <div className="min-h-screen bg-clinic-bg text-clinic-ink flex">
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-clinic-line min-h-screen p-6 sticky top-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-clinic-pine text-clinic-bg grid place-items-center font-heading">A</div>
          <div>
            <div className="font-heading text-sm">Admin</div>
            <div className="text-xs text-clinic-muted">{user.email}</div>
          </div>
        </div>
        <nav className="mt-10 flex flex-col gap-1 text-sm">
          <button onClick={()=>setTab("appointments")} data-testid="tab-appointments" className={`text-left px-3 py-2 rounded-lg ${tab==="appointments"?"bg-clinic-sage/50 text-clinic-pine":"hover:bg-clinic-sage/30"}`}>
            <CalendarDays size={16} className="inline mr-2" /> Appointments
          </button>
          <button onClick={()=>setTab("messages")} data-testid="tab-messages" className={`text-left px-3 py-2 rounded-lg ${tab==="messages"?"bg-clinic-sage/50 text-clinic-pine":"hover:bg-clinic-sage/30"}`}>
            <MessageSquare size={16} className="inline mr-2" /> Messages
          </button>
        </nav>
        <button onClick={()=>setPwOpen(true)} data-testid="open-change-password-btn" className="mt-auto inline-flex items-center gap-2 text-sm text-clinic-muted hover:text-clinic-pine">
          <KeyRound size={16} /> Change password
        </button>
        <button onClick={()=>{logout(); nav("/admin");}} data-testid="admin-logout-btn" className="mt-3 inline-flex items-center gap-2 text-sm text-clinic-muted hover:text-clinic-pine">
          <LogOut size={16} /> Sign out
        </button>
      </aside>

      <main className="flex-1 p-6 md:p-12 max-w-full">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl tracking-tight">Dashboard</h1>
            <p className="text-clinic-muted text-sm mt-1">Quiet control room. Manage your calendar, your way.</p>
          </div>
          <button onClick={load} data-testid="refresh-btn" className="btn-ghost text-sm">Refresh</button>
        </header>

        <section className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Total Bookings" value={stats.total} />
          <StatCard icon={Clock} label="Pending" value={stats.pending} accent />
          <StatCard icon={CheckCircle2} label="Confirmed" value={stats.confirmed} />
          <StatCard icon={CheckCircle2} label="Paid" value={stats.paid} pine />
        </section>

        {tab === "appointments" && (
          <section className="mt-12">
            <h2 className="font-heading text-xl mb-5">Appointments</h2>
            <div className="card-elevated overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="appointments-table">
                  <thead className="bg-clinic-sage/30 text-left text-xs uppercase tracking-[0.18em] text-clinic-muted">
                    <tr>
                      <th className="px-5 py-4">Patient</th>
                      <th className="px-5 py-4">Service</th>
                      <th className="px-5 py-4">Date / Time</th>
                      <th className="px-5 py-4">Payment</th>
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} className="px-5 py-10 text-center text-clinic-muted">Loading...</td></tr>
                    ) : appointments.length === 0 ? (
                      <tr><td colSpan={6} className="px-5 py-10 text-center text-clinic-muted">No appointments yet.</td></tr>
                    ) : appointments.map(a => (
                      <tr key={a.id} data-testid={`appt-row-${a.id}`} className="border-t border-clinic-line">
                        <td className="px-5 py-4">
                          <div className="font-medium">{a.name}</div>
                          <div className="text-xs text-clinic-muted">{a.email} · {a.phone}</div>
                        </td>
                        <td className="px-5 py-4">{a.service}</td>
                        <td className="px-5 py-4">{a.appointment_date}<div className="text-xs text-clinic-muted">{a.appointment_time}</div></td>
                        <td className="px-5 py-4">
                          <Badge tone={a.payment_status === "paid" ? "green" : "neutral"}>{a.payment_status}</Badge>
                        </td>
                        <td className="px-5 py-4">
                          <Badge tone={a.status === "confirmed" ? "green" : a.status === "cancelled" ? "red" : "amber"}>{a.status}</Badge>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end flex-wrap gap-2">
                            <a
                              href={buildAdminConfirmationUrl(a)}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`wa-confirm-${a.id}`}
                              title={`WhatsApp confirmation to ${a.phone}`}
                              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#25D366] text-white hover:bg-[#1FB854] transition"
                            >
                              <FaWhatsapp size={12} /> Confirm
                            </a>
                            {a.status !== "confirmed" && (
                              <button onClick={()=>updateStatus(a.id, "confirmed")} data-testid={`confirm-${a.id}`} className="text-xs px-3 py-1.5 rounded-full bg-clinic-pine text-clinic-bg hover:bg-clinic-pineHover">Mark confirmed</button>
                            )}
                            {a.status !== "cancelled" && (
                              <button onClick={()=>updateStatus(a.id, "cancelled")} data-testid={`cancel-${a.id}`} className="text-xs px-3 py-1.5 rounded-full border border-clinic-line hover:border-clinic-pine">Cancel</button>
                            )}
                            <button onClick={()=>removeAppt(a.id)} data-testid={`delete-${a.id}`} className="text-xs px-3 py-1.5 rounded-full border border-clinic-line hover:border-red-500 hover:text-red-500"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {tab === "messages" && (
          <section className="mt-12">
            <h2 className="font-heading text-xl mb-5">Contact messages</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {messages.length === 0 ? (
                <div className="text-clinic-muted text-sm">No messages yet.</div>
              ) : messages.map(m => (
                <div key={m.id} data-testid={`msg-${m.id}`} className="card-elevated p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{m.name}</div>
                      <div className="text-xs text-clinic-muted">{m.email} · {m.phone}</div>
                    </div>
                    <div className="text-xs text-clinic-muted">{new Date(m.created_at).toLocaleDateString()}</div>
                  </div>
                  <p className="text-sm text-clinic-ink/85 mt-4 leading-relaxed">{m.message}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
      {pwOpen && <ChangePasswordModal onClose={()=>setPwOpen(false)} onLoggedOut={()=>{logout(); nav("/admin");}} />}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent, pine }) {
  return (
    <div className={`card-elevated p-5 ${pine ? "bg-clinic-pine text-clinic-bg border-clinic-pine" : ""}`}>
      <div className={`w-9 h-9 rounded-full grid place-items-center ${pine ? "bg-clinic-bg/15 text-clinic-bg" : accent ? "bg-clinic-honey/30 text-clinic-honey" : "bg-clinic-sage/50 text-clinic-pine"}`}>
        <Icon size={18} />
      </div>
      <div className={`mt-4 font-heading text-3xl ${pine ? "text-clinic-bg" : "text-clinic-ink"}`}>{value}</div>
      <div className={`text-[11px] uppercase tracking-[0.18em] mt-1 ${pine ? "text-clinic-bg/70" : "text-clinic-muted"}`}>{label}</div>
    </div>
  );
}

function Badge({ children, tone = "neutral" }) {
  const map = {
    neutral: "bg-clinic-line text-clinic-ink/70",
    green: "bg-emerald-100 text-emerald-700",
    red: "bg-red-100 text-red-700",
    amber: "bg-amber-100 text-amber-700",
  };
  return <span className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-medium uppercase tracking-wide ${map[tone]}`}>{children}</span>;
}

function ChangePasswordModal({ onClose, onLoggedOut }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords don't match");
    if (next === current) return toast.error("New password must differ from current");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Please sign in again.");
      onClose();
      // Force re-login for safety
      setTimeout(() => onLoggedOut(), 600);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="change-password-modal" className="fixed inset-0 z-50 bg-clinic-ink/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e)=>e.stopPropagation()} onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-clinic-line p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-xl tracking-tight">Change password</h3>
          <button type="button" onClick={onClose} data-testid="close-pw-modal" className="text-clinic-muted hover:text-clinic-ink"><X size={18} /></button>
        </div>
        <p className="text-xs text-clinic-muted mt-1">You'll be signed out and asked to log in with the new password.</p>
        <div className="mt-5 space-y-3">
          <input data-testid="current-password-input" type="password" required autoComplete="current-password" placeholder="Current password" value={current} onChange={(e)=>setCurrent(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine" />
          <input data-testid="new-password-input" type="password" required autoComplete="new-password" placeholder="New password (min 8 chars)" value={next} onChange={(e)=>setNext(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine" />
          <input data-testid="confirm-password-input" type="password" required autoComplete="new-password" placeholder="Confirm new password" value={confirm} onChange={(e)=>setConfirm(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-clinic-line bg-white focus:outline-none focus:border-clinic-pine" />
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm text-clinic-muted hover:text-clinic-ink px-4 py-2">Cancel</button>
          <button type="submit" disabled={saving} data-testid="submit-change-password-btn" className="btn-primary text-sm justify-center disabled:opacity-60 disabled:cursor-wait">
            {saving ? "Updating..." : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}
